import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common'; // Import Location
import { ProfileService } from '../../core/services/profile.service';
import { State } from '../../core/models/state.model';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'; // Import ReactiveForms
import { CxIconComponent } from '../../shared/icon/cx-icon.component';
import { UserService } from '../../core/services/user.service';
import {
  getInternationalPhoneForSubmission,
  isValidInternationalPhone,
} from '../../shared/utils/phone-number.util';

declare var $: any; // Declare $ for jQuery/Bootstrap modal

@Component({
  selector: 'app-landing-request-demo-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CxIconComponent], // Add ReactiveFormsModule
  templateUrl: './landing-request-demo-page.component.html',
  styleUrl: './landing-request-demo-page.component.scss',
})
export class LandingRequestDemoPageComponent implements OnInit {
  statesList: State[] = [];
  demoForm: FormGroup;
  submitted = false;
  countries: any[] = [];
  filteredCountries: any[] = [];
  selectedCountry: any = null;
  countryCode = '+1';
  countryDropdownOpen = false;
  countrySearchTerm = '';
  private countriesLoading = false;

  constructor(
    private profileService: ProfileService,
    private userService: UserService,
    private location: Location,
    private fb: FormBuilder
  ) {
    this.demoForm = this.fb.group({
      fullName: ['', Validators.required],
      workEmail: ['', [Validators.required, Validators.email]],
      phone: ['', this.validatePhone],
      role: ['', Validators.required],
      organization: ['', Validators.required],
      country: ['United States'],
      state: [''],
      city: [''],
    });
  }

  ngOnInit(): void {
    this.fetchStateList();
    this.loadCountries();
  }

  fetchStateList() {
    this.profileService.fetchStateList().subscribe((res: any) => {
      if (res.success === 0) {
        this.statesList = res.data;
      }
    });
  }

  goBack() {
    this.location.back();
  }

  onSubmit() {
    this.submitted = true;
    if (this.demoForm.invalid) {
      return;
    }

    const formData = this.demoForm.value;
    const demoRequestDTO = {
      fullName: formData.fullName,
      workEmail: formData.workEmail,
      phone: this.getPhoneNumberForSubmission(formData.phone),
      role: formData.role,
      organizationName: formData.organization, // Mapping organization to organizationName
      country: formData.country,
      state: formData.state,
      city: formData.city,
    };

    this.profileService.demoRequest(demoRequestDTO).subscribe(
      (res: any) => {
        if (res.success === 0) {
          $('#demoRequestModal').modal('show');
          this.demoForm.reset();
          this.submitted = false;
          // Set default country back to United States after reset
          this.demoForm.get('country')?.setValue('United States');
          this.selectDefaultPhoneCountry();
        } else {
          // Handle error if needed
          console.error('Error submitting demo request:', res.message);
        }
      },
      (error) => {
        console.error('API Error:', error);
      }
    );
  }

  onModalClose() {
    $('#demoRequestModal').modal('hide');
    this.goBack();
  }

  toggleCountryDropdown(event: Event): void {
    event.stopPropagation();
    this.countryDropdownOpen = !this.countryDropdownOpen;

    if (this.countryDropdownOpen) {
      this.countrySearchTerm = '';
      this.filteredCountries = this.countries;
      this.loadCountries();
    }
  }

  onCountrySearch(event: Event): void {
    this.countrySearchTerm = (event.target as HTMLInputElement).value;
    this.filterCountries();
  }

  filterCountries(): void {
    const searchTerm = this.countrySearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      this.filteredCountries = this.countries;
      return;
    }

    this.filteredCountries = this.countries.filter(
      (country) =>
        country.name?.toLowerCase().includes(searchTerm) ||
        country.callingCode?.includes(searchTerm)
    );
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.countryCode = country.callingCode;
    this.countryDropdownOpen = false;
    this.countrySearchTerm = '';
    this.filteredCountries = this.countries;
    this.demoForm.get('phone')?.updateValueAndValidity({ emitEvent: false });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.cx-phone-country')) {
      this.countryDropdownOpen = false;
    }
  }

  private loadCountries(): void {
    if (this.countries.length > 0 || this.countriesLoading) {
      return;
    }

    this.countriesLoading = true;
    this.userService.getCountries(true).subscribe({
      next: (res: any) => {
        this.countriesLoading = false;
        if (!res || res.success !== 0 || !Array.isArray(res.data)) {
          return;
        }

        this.countries = res.data;
        this.filteredCountries = res.data;
        this.selectDefaultPhoneCountry();
      },
      error: () => {
        this.countriesLoading = false;
      },
    });
  }

  private selectDefaultPhoneCountry(): void {
    const unitedStates = this.countries.find(
      (country) =>
        country.callingCode === '+1' && country.countryIsoCode === 'US'
    );
    const selectedCountry = unitedStates ?? this.countries[0];

    if (selectedCountry) {
      this.selectedCountry = selectedCountry;
      this.countryCode = selectedCountry.callingCode;
    }
  }

  private getPhoneNumberForSubmission(phoneNumber: string): string {
    return getInternationalPhoneForSubmission(phoneNumber, this.countryCode);
  }

  private validatePhone = (control: AbstractControl): ValidationErrors | null =>
    isValidInternationalPhone(control.value, this.countryCode)
      ? null
      : { phone: true };
}
